import {
  Activity,
  CheckCheck,
  MessageSquareText,
  BookOpen,
  Bookmark,
  Brain,
  Calendar,
  CalendarPlus,
  ClipboardList,
  Clock,
  Eye,
  FilePlus,
  FileText,
  Files,
  FolderPlus,
  FolderMinus,
  Globe,
  History,
  ImageIcon,
  Instagram,
  LayoutTemplate,
  Lightbulb,
  Link,
  ListChecks,
  MessageCircleQuestion,
  Paperclip,
  BadgeCheck,
  Compass,
  Music2,
  Network,
  Pencil,
  Radio,
  RefreshCw,
  Rocket,
  ScrollText,
  Search,
  Send,
  MessagesSquare,
  Sparkles,
  Trash2,
  TrendingUp,
  Video,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import { chapterName, chapterSpec } from "../strategy/deck/deckModel";
import { scrubVendorTerms } from "@/lib/vendorTerms";

const INTERNAL_SKILL_LABELS: Record<string, string> = {
  brand_dna_standard: "Brand Universe Standard",
  strategy_meta: "Meta & Scope",
  strategy_category_landscape: "Category Landscape",
  strategy_audience_research: "Audience Insights & Personas",
  strategy_competitor_landscape: "Competitor Analysis & Gap",
  strategy_brand_audit: "Brand Audit",
  strategy_trend_landscape: "Cultural, Platform & Format Trends",
  strategy_ugc_social_listening: "UGC & Social Listening",
  strategy_combined_insights: "Combined Insights",
  strategy_objectives: "Social Objectives",
  strategy_channel_strategy: "Channel Strategy & Exclusions",
  strategy_content_strategy: "Content Strategy / Pillars",
  strategy_measurement: "Metrics",
  strategy_roadmap: "Roadmap",
  strategy_appendix: "Appendix",
};

const STRATEGY_SECTION_LABELS: Record<string, string> = {
  meta: "Meta & scope",
  research: "Research",
  combined_insights: "Combined insights",
  objectives: "Objectives",
  channel_strategy: "Channel strategy",
  channels_excluded: "Excluded channels",
  content_strategy: "Content strategy",
  measurement: "Measurement",
  roadmap: "Roadmap",
  appendix: "Appendix",
};

const STATIC_TOOL_LABELS: Record<string, string> = {
  PerplexitySearch: "Web search",
  QueryBrandDNA: "Brand universe",
  instagram_data: "Instagram research",
  tiktok_data: "TikTok research",
  URLScraper: "URL scraper",
  GetStrategyDocument: "Read strategy",
  GetChapterShape: "Read a chapter's fields",
  SetSlideText: "Wrote a slide's lines",
  CreateStrategy: "Started the strategy",
  AskUserQuestion: "Asked you a question",
  Navigate: "Took you there",
  VerifyWithUser: "Asked you to check what it found",
  GenerateImage: "Generate an image",
  SaveImageFromUrl: "Saved a picture to Brand Media",
  ListThreads: "Listed threads",
  SearchThreads: "Searched threads",
  ReadThread: "Read thread",
};

/** Short mono chip shown beside the human label in the chat rail. */
const TOOL_INSTRUMENT_LABELS: Record<string, string> = {
  PerplexitySearch: "SEARCH",
  QueryBrandDNA: "UNIVERSE",
  instagram_data: "IG",
  tiktok_data: "TIKTOK",
  URLScraper: "URL",
  AnalyzeLink: "LINK",
  AttachVideoFromLink: "VIDEO",
  ProposeFact: "PROPOSE",
  ListProposedFacts: "LIST",
  EditProposedFact: "EDIT",
  DeleteProposedFact: "DELETE",
  SetSkills: "SKILLS",
  GetSocialVideos: "VIDEO",
  GetSocialChannels: "CHANNEL",
  GetStrategyDocument: "STRATEGY",
  GetChapterShape: "STRATEGY",
  WriteStrategyChapter: "STRATEGY",
  SetSlideText: "STRATEGY",
  SetStrategySection: "STRATEGY",
  CreateStrategy: "STRATEGY",
  InternalSkillCallingTool: "SKILL",
  LoadSkill: "SKILL",
  ExitSkill: "SKILL",
  AskUserQuestion: "ASK",
  Navigate: "GO",
  VerifyWithUser: "VERIFY",
  GenerateImage: "IMAGE",
  SaveImageFromUrl: "IMAGE",
  CreateArtifact: "ARTIFACT",
};

export const TOOL_ICON_MAP: Record<string, { icon: LucideIcon; className: string }> = {
  ProposeFact: { icon: FilePlus, className: "text-emerald-400/75" },
  ListProposedFacts: { icon: ListChecks, className: "text-emerald-400/75" },
  EditProposedFact: { icon: Pencil, className: "text-emerald-400/75" },
  DeleteProposedFact: { icon: Trash2, className: "text-red-400/65" },
  SetSkills: { icon: Sparkles, className: "text-violet-400/75" },
  QueryBrandDNA: { icon: Network, className: "text-cyan-400/75" },
  SaveMemory: { icon: Brain, className: "text-cyan-400/75" },
  SaveImageFromUrl: { icon: ImageIcon, className: "text-cyan-400/75" },
  SavePersonalMemory: { icon: Brain, className: "text-violet-400/75" },
  PerplexitySearch: { icon: Globe, className: "text-sky-400/75" },
  instagram_data: { icon: Instagram, className: "text-pink-400/75" },
  tiktok_data: { icon: Music2, className: "text-white/55" },
  URLScraper: { icon: Link, className: "text-sky-400/75" },
  AnalyzeLink: { icon: Eye, className: "text-violet-400/75" },
  GetSocialVideos: { icon: Video, className: "text-amber-400/75" },
  GetSocialChannels: { icon: Radio, className: "text-amber-400/75" },
  SearchTrends: { icon: TrendingUp, className: "text-amber-400/75" },
  GetTrendDetail: { icon: TrendingUp, className: "text-amber-400/75" },
  SearchReferences: { icon: Bookmark, className: "text-sky-400/75" },
  GetReferenceDetail: { icon: Bookmark, className: "text-sky-400/75" },
  GetMarketSignals: { icon: Activity, className: "text-amber-400/75" },
  SetMarketSignal: { icon: Activity, className: "text-amber-400/75" },
  QueryDocuments: { icon: Search, className: "text-white/45" },
  ReadDocument: { icon: BookOpen, className: "text-white/45" },
  ListChatDocuments: { icon: Files, className: "text-white/45" },
  AskVideo: { icon: Video, className: "text-amber-400/75" },
  AttachVideoFromLink: { icon: Paperclip, className: "text-amber-400/75" },
  InternalSkillCallingTool: { icon: Sparkles, className: "text-violet-400/80" },
  LoadSkill: { icon: Sparkles, className: "text-violet-400/80" },
  ExitSkill: { icon: Sparkles, className: "text-violet-400/50" },
  SetStrategySection: { icon: FileText, className: "text-co-accent-ink" },
  GetStrategyDocument: { icon: FileText, className: "text-white/45" },
  GetChapterShape: { icon: FileText, className: "text-white/45" },
  WriteStrategyChapter: { icon: FileText, className: "text-co-accent-ink" },
  SetSlideText: { icon: Pencil, className: "text-co-accent-ink" },
  GetActiveStrategy: { icon: FileText, className: "text-white/45" },
  CreateStrategy: { icon: FilePlus, className: "text-co-accent-ink" },
  SetSlideMedia: { icon: ImageIcon, className: "text-co-accent-ink" },
  AskUserQuestion: { icon: MessageCircleQuestion, className: "text-amber-400/80" },
  Navigate: { icon: Compass, className: "text-co-accent-ink" },
  VerifyWithUser: { icon: BadgeCheck, className: "text-amber-400/80" },
  GenerateImage: { icon: ImageIcon, className: "text-co-accent-ink" },
  CreateArtifact: { icon: FileText, className: "text-co-accent-ink" },
  UpdateArtifact: { icon: FileText, className: "text-co-accent-ink" },
  EditArtifact: { icon: Pencil, className: "text-co-accent-ink" },
  ListArtifacts: { icon: Files, className: "text-white/45" },
  GetArtifact: { icon: FileText, className: "text-white/45" },
  ListArtifactNotes: { icon: MessageSquareText, className: "text-white/45" },
  ResolveArtifactNote: { icon: CheckCheck, className: "text-co-accent-ink" },
  ListConceptIdeas: { icon: Lightbulb, className: "text-amber-400/75" },
  GetConceptIdea: { icon: Lightbulb, className: "text-amber-400/75" },
  SaveConceptIdea: { icon: Lightbulb, className: "text-co-accent-ink" },
  UpdateConceptIdea: { icon: Pencil, className: "text-co-accent-ink" },
  GetConceptBrief: { icon: ClipboardList, className: "text-amber-400/75" },
  SaveConceptBrief: { icon: ClipboardList, className: "text-co-accent-ink" },
  UpdateConceptBrief: { icon: Pencil, className: "text-co-accent-ink" },
  UpdateConcept: { icon: Pencil, className: "text-co-accent-ink" },
  WriteConcept: { icon: Lightbulb, className: "text-co-accent-ink" },
  GetPostConceptHistory: { icon: History, className: "text-white/45" },
  StartMonthPlan: { icon: Calendar, className: "text-co-accent-ink" },
  GetCalendarMonth: { icon: Calendar, className: "text-white/45" },
  ListCalendarMonths: { icon: Calendar, className: "text-white/45" },
  AddPost: { icon: CalendarPlus, className: "text-emerald-400/75" },
  UpdatePost: { icon: Pencil, className: "text-co-accent-ink" },
  DeletePost: { icon: Trash2, className: "text-red-400/65" },
  SetPostLink: { icon: Link, className: "text-co-accent-ink" },
  GetSlotBrief: { icon: ClipboardList, className: "text-white/45" },
  EditBrief: { icon: ClipboardList, className: "text-co-accent-ink" },
  RecoverStuckWork: { icon: RefreshCw, className: "text-amber-400/75" },
  ListWorkshopAgents: { icon: LayoutTemplate, className: "text-white/45" },
  ListDeployments: { icon: Rocket, className: "text-white/45" },
  GetDeployment: { icon: Rocket, className: "text-white/45" },
  GetAgentRunStatus: { icon: Activity, className: "text-white/45" },
  WaitForAgentRuns: { icon: Clock, className: "text-amber-400/75" },
  WaitForAgents: { icon: Clock, className: "text-amber-400/75" },
  GetAgentConversation: { icon: MessagesSquare, className: "text-white/45" },
  MessageAgent: { icon: Send, className: "text-co-accent-ink" },
  DeployWorkshopAgent: { icon: Rocket, className: "text-co-accent-ink" },
  ListContextItems: { icon: Files, className: "text-white/45" },
  AddContextItem: { icon: FolderPlus, className: "text-emerald-400/75" },
  RemoveContextItem: { icon: FolderMinus, className: "text-red-400/65" },
  UpdateProject: { icon: Pencil, className: "text-co-accent-ink" },
  ListThreads: { icon: MessagesSquare, className: "text-white/45" },
  SearchThreads: { icon: Search, className: "text-white/45" },
  ReadThread: { icon: ScrollText, className: "text-white/45" },
};

export const DEFAULT_TOOL_ICON = {
  icon: Wrench,
  className: "text-white/40",
} as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function humanizeSnakeCase(value: string): string {
  return value
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function cleanSkillTitle(title: string): string {
  return title
    .replace(/^Strategy:\s*/i, "")
    .replace(/\s*\(Section [^)]+\)$/i, "")
    .replace(/\bBrand DNA\b/gi, "Brand Universe")
    .trim();
}

function formatStrategySection(section: string): string {
  return STRATEGY_SECTION_LABELS[section] ?? humanizeSnakeCase(section);
}

/** "brief" → "the brief", "culture" → "culture and conversation", "summary" → "the one page". */
function formatStrategyChapter(chapter: string): string {
  if (chapter === "summary") return "the one page";
  const spec = chapterSpec(chapter);
  if (!spec) return humanizeSnakeCase(chapter).toLowerCase();
  const title = chapterName(spec);
  return title.charAt(0).toLowerCase() + title.slice(1);
}

function extractStrategyChapter(input: unknown, result: unknown): string | null {
  if (isRecord(input) && typeof input.chapter === "string") return input.chapter;
  if (isRecord(result) && typeof result.chapter === "string") return result.chapter;
  return null;
}

function extractStrategySection(input: unknown, result: unknown): string | null {
  if (isRecord(input) && typeof input.section === "string") return input.section;
  if (isRecord(result) && typeof result.section === "string") return result.section;
  return null;
}

function extractSkillLabels(input: unknown, result: unknown): string[] {
  if (isRecord(result) && Array.isArray(result.skills)) {
    const titles = result.skills
      .map((skill) => (isRecord(skill) && typeof skill.title === "string" ? cleanSkillTitle(skill.title) : null))
      .filter((title): title is string => Boolean(title));
    if (titles.length > 0) return titles;
  }

  if (isRecord(input) && Array.isArray(input.skills)) {
    return input.skills
      .filter((skill): skill is string => typeof skill === "string")
      .map((skill) => INTERNAL_SKILL_LABELS[skill] ?? humanizeSnakeCase(skill));
  }

  return [];
}

function attachVideoUrls(input: unknown): string[] {
  if (!isRecord(input)) return [];
  if (Array.isArray(input.videos)) {
    return input.videos.flatMap((item) =>
      isRecord(item) && typeof item.url === "string" ? [item.url] : [],
    );
  }
  if (Array.isArray(input.urls)) {
    return input.urls.filter((u): u is string => typeof u === "string");
  }
  return [];
}

/** A bare "the link" says nothing on a rail carrying five of them. */
function extractLinkHost(input: unknown, result: unknown): string | null {
  const raw =
    (isRecord(result) && typeof result.url === "string" ? result.url : null) ??
    (isRecord(input) && typeof input.url === "string" ? input.url : null);
  if (!raw) return null;
  try {
    return new URL(raw).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

/** Workshop skills: the result carries the display name, the input only the key. */
function extractWorkshopSkillName(input: unknown, result: unknown): string | null {
  if (isRecord(result) && typeof result.name === "string" && result.name) {
    return result.name;
  }
  if (isRecord(input) && typeof input.key === "string" && input.key) return input.key;
  return null;
}

/** Split camelCase / PascalCase without blowing up acronyms (URLScraper → "URL Scraper"). */
function defaultCamelCaseLabel(name: string): string {
  return name
    .replace(/([a-z\d])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
}

export function getToolDisplayLabel(
  toolName: string,
  input?: unknown | null,
  result?: unknown | null,
): string {
  if (toolName === "InternalSkillCallingTool") {
    const skills = extractSkillLabels(input, result);
    if (skills.length > 0) return `Activated skill: ${skills.join(", ")}`;
    return "Activated skill";
  }

  if (toolName === "WriteStrategyChapter") {
    const chapter = extractStrategyChapter(input, result);
    return chapter ? `Wrote ${formatStrategyChapter(chapter)}` : "Wrote a chapter";
  }

  if (toolName === "SetStrategySection") {
    const section = extractStrategySection(input, result);
    if (section) return `Update strategy: ${formatStrategySection(section)}`;
    return "Update strategy";
  }

  if (toolName === "LoadSkill" || toolName === "ExitSkill") {
    const verb = toolName === "LoadSkill" ? "Loaded" : "Exited";
    const name = extractWorkshopSkillName(input, result);
    return name ? `${verb} skill: ${name}` : `${verb} skill`;
  }

  if (toolName === "AnalyzeLink") {
    const type = isRecord(result) && typeof result.type === "string" ? result.type : null;
    const verb = type === "video" ? "Watched" : type === "image" ? "Looked at" : "Read";
    const host = extractLinkHost(input, result);
    return host ? `${verb} ${host}` : `${verb} the link`;
  }

  if (toolName === "AttachVideoFromLink") {
    const urls = attachVideoUrls(input);
    const host = urls.length === 1 ? extractLinkHost({ url: urls[0] }, null) : null;
    if (!isRecord(result)) {
      if (urls.length > 1) return `Attaching ${urls.length} videos`;
      return host ? `Attaching video from ${host}` : "Attaching a video";
    }
    const attached = typeof result.attached === "number" ? result.attached : 0;
    const failed = typeof result.failed === "number" ? result.failed : 0;
    const total = attached + failed;
    if (attached === 0) return total > 1 ? "Could not attach the videos" : "Could not attach that video";
    if (failed > 0) return `Attached ${attached} of ${total} videos`;
    if (attached > 1) return `Attached ${attached} videos`;
    return host ? `Attached video from ${host}` : "Attached a video";
  }

  if (toolName === "Navigate") {
    // The tool ends the turn and the rail routes on it, so the chip is the only
    // trace in the transcript of why the page changed underneath the user.
    const label = isRecord(result) && typeof result.label === "string" ? result.label : null;
    if (isRecord(result) && result.ok === false) return "Could not open that page";
    return label ? `Took you to ${label}` : "Took you there";
  }

  if (toolName === "CreateArtifact") {
    const title =
      (isRecord(result) && typeof result.title === "string" ? result.title : null) ??
      (isRecord(input) && typeof input.title === "string" ? input.title : null);
    return title ? `Created artifact: ${title}` : "Created an artifact";
  }

  if (toolName === "UpdateProject") {
    const name =
      (isRecord(result) && typeof result.name === "string" ? result.name : null) ??
      (isRecord(input) && typeof input.name === "string" ? input.name : null);
    return name ? `Updated the project: ${name}` : "Updated the project";
  }

  const staticLabel = STATIC_TOOL_LABELS[toolName];
  if (staticLabel) return staticLabel;

  return scrubVendorTerms(defaultCamelCaseLabel(toolName));
}

/** Compact uppercase instrument for the rail act line (left of the human label). */
export function getToolInstrumentLabel(
  toolName: string,
  input?: unknown | null,
  result?: unknown | null,
): string {
  const mapped = TOOL_INSTRUMENT_LABELS[toolName];
  if (mapped) return mapped;

  if (toolName === "SetStrategySection") return "STRATEGY";
  if (toolName === "InternalSkillCallingTool") {
    const skills = extractSkillLabels(input, result);
    if (skills[0]) return skills[0].split(/\s+/)[0]!.toUpperCase().slice(0, 10);
    return "SKILL";
  }

  const label = getToolDisplayLabel(toolName, input, result);
  const word = label.split(/\s+/).find((w) => w.length > 1) ?? label.split(/\s+/)[0] ?? toolName;
  return word.toUpperCase().slice(0, 12);
}

export function getToolIcon(toolName: string) {
  return TOOL_ICON_MAP[toolName] ?? DEFAULT_TOOL_ICON;
}

/** @deprecated Use getToolDisplayLabel instead. */
export function formatToolName(name: string) {
  return getToolDisplayLabel(name);
}
